/**
 * #53 PR 3 / DEC-360 — per-driver control of the Map View overlays (design §5b).
 *
 *   - Eye (hide): hides that driver's pickup, disposal and route. Their truck PIN STAYS,
 *     so a dispatcher can never lose a truck by accident (Corbin, C2).
 *   - Focus: overlays only for the focused driver(s); everyone else's pin is DIMMED, not
 *     removed (C3). Focusing more drivers adds to the set; "Show all" clears it.
 *   - Neither is remembered: both reset on reload (C4). The driver set changes daily, and
 *     a remembered hide would silently hide someone next week.
 *
 * Pure, so the rule is unit-tested apart from the map.
 */
import type { DriverPosition } from '@lodex-pro/shared';

export interface DriverOverlayState {
  readonly hidden: ReadonlySet<string>;
  readonly focused: ReadonlySet<string>;
}

export const NO_DRIVER_FILTER: DriverOverlayState = { hidden: new Set(), focused: new Set() };

/** Whether this driver's overlays (sites + route) are drawn. A hide always wins. */
export function showsOverlays(driverId: string, state: DriverOverlayState): boolean {
  if (state.hidden.has(driverId)) return false;
  return state.focused.size === 0 || state.focused.has(driverId);
}

/** The positions whose overlays are drawn — what the layer builder is given. */
export function overlayPositions(
  positions: ReadonlyArray<DriverPosition>,
  state: DriverOverlayState,
): ReadonlyArray<DriverPosition> {
  if (state.hidden.size === 0 && state.focused.size === 0) return positions;
  return positions.filter((p) => showsOverlays(p.driverId, state));
}

/** A pin is dimmed only while Focus is on and this driver is not focused. Never removed. */
export function pinDimmed(driverId: string, state: DriverOverlayState): boolean {
  return state.focused.size > 0 && !state.focused.has(driverId);
}

/**
 * How many of the drivers CURRENTLY on the map are hidden / focused. Ids of drivers who
 * have left the feed still sit in the sets (harmless), but must not inflate "2 hidden".
 */
export function presentCounts(
  positions: ReadonlyArray<DriverPosition>,
  state: DriverOverlayState,
): { readonly hidden: number; readonly focused: number } {
  let hidden = 0;
  let focused = 0;
  for (const p of positions) {
    if (state.hidden.has(p.driverId)) hidden += 1;
    if (state.focused.has(p.driverId)) focused += 1;
  }
  return { hidden, focused };
}

/**
 * The state restricted to drivers currently on the map. Apply this BEFORE the rules above:
 * if the only focused driver drops off the feed (no fix for 24 h), a raw Focus would stay
 * "on" for nobody — every pin dimmed and every overlay gone, with nothing to click to undo
 * it. Pruned, Focus simply ends.
 */
export function pruneToPresent(
  positions: ReadonlyArray<DriverPosition>,
  state: DriverOverlayState,
): DriverOverlayState {
  if (state.hidden.size === 0 && state.focused.size === 0) return state;
  const present = new Set(positions.map((p) => p.driverId));
  const keep = (s: ReadonlySet<string>): ReadonlySet<string> =>
    new Set([...s].filter((id) => present.has(id)));
  return { hidden: keep(state.hidden), focused: keep(state.focused) };
}

/** Toggle one id in a set, returning a NEW set (React state). */
export function toggled(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}
